import {
  WhatsappDraftTemplate,
  WhatsappFrontTemplateObject,
  WhatsappOriginalTemplate,
  WhatsappTemplate,
  WhatsappTemplateMessageComponents,
} from 'types/templates';
import { TemplateVarsData } from 'types/templates/templates-menu';
import { fetcher, toCamelCaseKeys } from 'utils/axios';
import { consumeMessagesQuota } from 'utils/messagesQuota/consume';
import { metaComponentsArrayToFlatComponents } from 'utils/template-message-doc';

function normalizeSpecItems(spec: unknown): TemplateVarsData {
  if (!Array.isArray(spec)) return [];
  const out: TemplateVarsData = [];
  for (const item of spec) {
    if (item == null || typeof item !== 'object') continue;
    const n = toCamelCaseKeys(item) as Record<string, unknown>;
    if (typeof n.name !== 'string' || !n.name) continue;
    out.push(n as TemplateVarsData[number]);
  }
  return out;
}

/** מחפש מערך של אובייקטים עם שדה name (spec) בעומק מוגבל */
function findSpecArrayDeep(obj: unknown, depth = 0): unknown[] | null {
  if (depth > 6 || obj == null || typeof obj !== 'object') return null;
  if (Array.isArray(obj)) return null;
  const rec = obj as Record<string, unknown>;
  for (const v of Object.values(rec)) {
    if (Array.isArray(v) && v.length > 0) {
      const first = v[0];
      if (first && typeof first === 'object') {
        const f = toCamelCaseKeys(first) as Record<string, unknown>;
        if (typeof f.name === 'string' && f.name) return v;
      }
    }
  }
  for (const v of Object.values(rec)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const found = findSpecArrayDeep(v, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

/**
 * מחלץ את מערך הפרמטרים מתוך תגובת GetTemplateWithMediaDataFromObject.
 * (לעיתים spec תחת data/result, או שמות מפתחות חלופיים — כמו ב־BotBuilder: res.data.spec)
 */
function extractTemplateVarsSpecFromResponse(camelRoot: unknown): TemplateVarsData {
  if (camelRoot == null || typeof camelRoot !== 'object' || Array.isArray(camelRoot)) return [];
  const normalized = camelRoot as Record<string, unknown>;

  const tryPick = (obj: Record<string, unknown>): TemplateVarsData => {
    const keys = ['spec', 'templateSpec', 'variableSpec', 'variables', 'params', 'templateVariables'];
    for (const k of keys) {
      const arr = obj[k];
      if (Array.isArray(arr) && arr.length > 0) {
        const items = normalizeSpecItems(arr);
        if (items.length > 0) return items;
      }
    }
    return [];
  };

  let spec = tryPick(normalized);
  if (spec.length > 0) return spec;

  if (normalized.data != null && typeof normalized.data === 'object') {
    spec = tryPick(toCamelCaseKeys(normalized.data) as Record<string, unknown>);
    if (spec.length > 0) return spec;
  }
  if (normalized.result != null && typeof normalized.result === 'object') {
    spec = tryPick(toCamelCaseKeys(normalized.result) as Record<string, unknown>);
    if (spec.length > 0) return spec;
  }

  const deep = findSpecArrayDeep(camelRoot);
  return deep ? normalizeSpecItems(deep) : [];
}

export async function updateTemplateHebrewName(templateOriginalName: string, templateHebrewName: string) {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/services/updateWhatsappTemplateName',
        {
          data: {
            templateOriginalName,
            templateHebrewName,
          },
        },
      ],
      'history',
    );
    return data;
  } catch (error) {
    console.warn('updateTemplateHebrewName error: ', error);
    return null;
  }
}
export async function fetchTemplateVarsData(template: WhatsappTemplate) {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/make/GetTemplateWithMediaDataFromObject',
        {
          data: {
            templateObject: template,
          },
        },
      ],
      'app',
    );
    /** מסלול app: PascalCase / spec מקונן — לא משפיע על GetTemplateFormattedObject / שליחה */
    if (data != null && typeof data === 'object') {
      const camel = toCamelCaseKeys(data) as Record<string, unknown>;
      const spec = extractTemplateVarsSpecFromResponse(camel);
      return { ...camel, spec } as typeof data & { spec: TemplateVarsData };
    }
    return data;
  } catch (error) {
    console.warn('getTemplateVarsData error: ', error);
    return null;
  }
}

export type GetTemplateFormattedObjectParams = {
  templateObject?: WhatsappTemplate;
  templateOriginalObject: WhatsappOriginalTemplate;
  to: string;
  templateVars: Record<TemplateVarsData[0]['name'], string>;
  fileName?: string;
};

/**
 * תשובת GetTemplateFormattedObject: אובייקט שטוח או מערך רכיבי Meta — מנרמל ל־BODY/HEADER וכו'.
 */
function normalizeTemplateMessageComponentsPayload(raw: unknown): WhatsappTemplateMessageComponents | null {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    return metaComponentsArrayToFlatComponents(raw);
  }
  if (typeof raw !== 'object') return null;
  const n = toCamelCaseKeys(raw) as Record<string, unknown>;
  const nested = n.components;
  if (Array.isArray(nested)) {
    return metaComponentsArrayToFlatComponents(nested);
  }
  const header = n.header ?? n.HEADER;
  const body = n.body ?? n.BODY;
  const footer = n.footer ?? n.FOOTER;
  const buttons = n.buttons ?? n.BUTTONS;
  if (body == null || typeof body !== 'object') return null;
  return {
    ...(header != null ? { HEADER: header as WhatsappTemplateMessageComponents['HEADER'] } : {}),
    BODY: body as WhatsappTemplateMessageComponents['BODY'],
    ...(footer != null ? { FOOTER: footer as WhatsappTemplateMessageComponents['FOOTER'] } : {}),
    ...(buttons != null ? { BUTTONS: buttons as WhatsappTemplateMessageComponents['BUTTONS'] } : {}),
  } as WhatsappTemplateMessageComponents;
}

export async function getTemplateFormattedObject(params: GetTemplateFormattedObjectParams) {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/make/GetTemplateFormattedObject',
        {
          data: params,
        },
      ],
      'app',
    );
    if (data == null || typeof data !== 'object') return null;
    const raw = data as Record<string, unknown>;
    const rawFront = raw.frontTemplateObj ?? raw.FrontTemplateObj;
    const rawComp = raw.components ?? raw.Components;
    const frontTemplateObj = toCamelCaseKeys(rawFront) as WhatsappFrontTemplateObject;
    const components = normalizeTemplateMessageComponentsPayload(rawComp);
    if (!components?.BODY) {
      console.warn('getTemplateFormattedObject: could not normalize components');
      return null;
    }
    return {
      frontTemplateObj,
      components,
    };
  } catch (error) {
    console.warn('getTemplateFormattedObject error: ', error);
    return null;
  }
}

export type FetchSendTemplateMessageParams = {
  id: string;
  templateName: string;
  languageCode: string;
  message: string;
  components: Array<{
    type: string;
    parameters: Array<{
      type: string;
      text?: string;
      document?: { link: string; filename?: string };
      image?: { link: string };
      video?: { link: string };
    }>;
    /** Meta — כפתורי URL / quick_reply */
    sub_type?: string;
    index?: number;
  }>;
  TempId?: string;
  procData?: string | null;
};
export async function fetchSendTemplateMessage(params: FetchSendTemplateMessageParams) {
  if (!consumeMessagesQuota(1)) {
    return { result: false, message: 'messages_quota_exceeded' };
  }

  try {
    const data = await fetcher(
      'post',
      [
        '/api/whatsapp/SendTemplateMessage',
        {
          data: params,
        },
      ],
      'messages',
    );
    if (data)
      return data as {
        result: boolean;
        message: string;
      };
    else return null;
  } catch (error) {
    console.warn('fetchSendTemplateMessage error: ', error);
    return null;
  }
}

//* drafts
export async function getDraftTemplates() {
  try {
    const data = await fetcher('get', '/api/wa-business/getDraftTemplates', 'history');

    if (data)
      return data as {
        result: boolean;
        templates: Array<WhatsappDraftTemplate>;
      };
    else return null;
  } catch (error) {
    console.warn('getDraftTemplates error: ', error);
    return null;
  }
}
export async function createUpdateTemplateDraft(
  templateOriginalName: string,
  templateHebrewName: string,
  DraftTemplatObject: string,
  draftId?: number,
) {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/wa-business/createUpdateTemplateDraft',
        {
          data: {
            templateOriginalName,
            templateHebrewName,
            DraftTemplatObject,
            draftId,
          },
        },
      ],
      'history',
    );
    if (data)
      return data as {
        result: boolean;
        id: number;
      };
    else return null;
  } catch (error) {
    console.warn('createUpdateTemplateDraft error: ', error);
    return null;
  }
}
export async function deleteDraftTemplate(id: number) {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/wa-business/deleteTemplateDraft',
        {
          data: {
            draftId: id,
          },
        },
      ],
      'history',
    );
    if (data)
      return data as {
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('deleteDraftTemplate error: ', error);
    return null;
  }
}
export async function fetchGetDraftTemplateById(id: string) {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/wa-business/getDraftTemplatebyId',
        {
          data: {
            id,
          },
          // skipServerContext: true,
        },
      ],
      'history',
    );
    if (data)
      return data as {
        result: boolean;
        template: WhatsappDraftTemplate;
      };
    else return null;
  } catch (error) {
    console.warn('fetchGetDraftTemplateById error: ', error);
    return null;
  }
}
